import React, { useState, useEffect } from 'react'
import {
  Globe2,
  Clock3,
  ShieldCheck,
  Headset,
  ArrowUpRight
} from 'lucide-react'

import TruckImage1 from '../assets/images/data/Conveyor-boxes.png'
import TruckImage2 from '../assets/images/data/Forklift.png'
import TruckImage3 from '../assets/images/data/Parcels-Scanning .png'

const truckImages = [TruckImage1, TruckImage2, TruckImage3]

const features = [
  {
    icon: Globe2,
    title: 'Wide Network',
    description: 'Pan-India & global connectivity'
  },
  {
    icon: Clock3,
    title: 'On-Time Delivery',
    description: 'Your time matters to us'
  },
  {
    icon: ShieldCheck,
    title: 'Safe & Secure',
    description: 'Advanced tracking & handling'
  },
  {
    icon: Headset,
    title: 'Dedicated Support',
    description: 'Always here for you'
  }
]

const WhyChooseUs = () => {
  const [currentImage, setCurrentImage] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage(prev => (prev + 1) % truckImages.length)
    }, 4000)

    return () => clearInterval(interval)
  }, [])

  return (
    <section className='relative w-full overflow-hidden bg-[#171b50]'>
      <div
        className='
        relative grid w-full
        grid-cols-1
        lg:grid-cols-[30%_40%_30%]
        items-stretch
      '
      >
        {/* LEFT CONTENT */}
        <div
          className='
          relative z-10
          flex flex-col justify-center
          px-5 py-12
          sm:px-8 sm:py-14
          md:px-12
          lg:px-7 lg:py-10
          xl:px-12
          2xl:px-16
        '
        >
          <div className='mb-4 flex items-center gap-2'>
            <span className='h-[2px] w-7 bg-[#ffe331]' />

            <span
              className='
              text-[10px] sm:text-[11px]
              font-bold uppercase
              tracking-[3px] text-white/65
            '
            >
              Why Choose Us
            </span>
          </div>

          <h2
            className='
            text-[28px]
            sm:text-[32px]
            md:text-[38px]
            lg:text-[29px]
            xl:text-[36px]
            2xl:text-[40px]
            font-bold
            leading-[1.15]
            tracking-[-1px]
            text-white
          '
          >
            More Than Logistics,
            <br />
            We Build <span className='text-[#ffe331]'>Partnerships.</span>
          </h2>

          <p
            className='
            mt-4 max-w-[390px]
            text-[13px] sm:text-[14px]
            leading-[1.9]
            text-white/65
          '
          >
            S Infinite Express delivers smart, reliable logistics across India
            and beyond.
          </p>

          <a
            href='/about-us/'
            className='
              group mt-6
              inline-flex w-fit items-center gap-4
              rounded-full
              bg-[#ffe331]
              py-2.5 pl-5 pr-3
              text-[11px] sm:text-xs
              font-bold text-[#171b50]
              transition-all duration-300
              hover:bg-white
            '
          >
            Discover Our Story
            <span
              className='
              flex h-8 w-8
              items-center justify-center
              rounded-full
              bg-[#171b50] text-white
              transition-transform duration-300
              group-hover:translate-x-1
            '
            >
              <ArrowUpRight size={16} />
            </span>
          </a>
        </div>

        {/* CENTER FEATURES */}
        <div
          className='
          relative z-10
          flex items-center
          px-4 py-8
          sm:px-8 sm:py-10
          lg:px-2 lg:py-12
          xl:px-4
        '
        >
          <div
            className='
            grid w-full
            grid-cols-2
            lg:grid-cols-2
            xl:grid-cols-4
          '
          >
            {features.map((feature, index) => {
              const Icon = feature.icon

              return (
                <div
                  key={feature.title}
                  className={`
                    group
                    flex flex-col
                    min-h-[145px]
                    sm:min-h-[160px]
                    lg:min-h-[150px]
                    xl:min-h-[175px]
                    items-center justify-center
                    px-2 sm:px-4
                    py-5
                    text-center
                    transition-colors duration-300
                    hover:bg-white/[0.04]

                    ${index % 2 !== 0 ? 'border-l border-white/15' : ''}

                    ${index >= 2 ? 'border-t border-white/15' : ''}

                    xl:border-t-0
                    xl:border-l
                    ${index === 0 ? 'xl:border-l-0' : ''}
                  `}
                >
                  {/* Icon */}
                  <div
                    className='
                    mb-4
                    flex h-11 w-11
                    sm:h-12 sm:w-12
                    items-center justify-center
                    rounded-full
                    border border-white/15
                    text-[#ffe331]
                    transition-all duration-300
                    group-hover:border-[#ffe331]
                    group-hover:bg-[#ffe331]
                    group-hover:text-[#171b50]
                  '
                  >
                    <Icon size={21} strokeWidth={1.6} />
                  </div>

                  {/* Title */}
                  <h3
                    className='
                    text-[11px]
                    sm:text-xs
                    lg:text-[11px]
                    xl:text-xs
                    font-bold text-white
                  '
                  >
                    {feature.title}
                  </h3>

                  {/* Description */}
                  <p
                    className='
                    mt-2
                    max-w-[145px]
                    text-[10px]
                    sm:text-[11px]
                    leading-[1.7]
                    text-white/50
                  '
                  >
                    {feature.description}
                  </p>
                </div>
              )
            })}
          </div>
        </div>

        {/* RIGHT IMAGE AREA */}
        <div
          className='
          relative
          min-h-[260px]
          sm:min-h-[340px]
          md:min-h-[400px]
          lg:min-h-[450px]
          xl:min-h-full
          w-full
          overflow-hidden
        '
        >
          {/* Yellow diagonal background */}
          <div
            className='absolute inset-0 bg-[#ffe331]'
            style={{
              clipPath: 'polygon(18% 0, 100% 0, 100% 100%, 0 100%)'
            }}
          />

          {/* Image slider */}
          <div
            className='absolute inset-0 overflow-hidden bg-[#171b50]'
            style={{
              clipPath: 'polygon(23% 0, 100% 0, 100% 100%, 0 100%)'
            }}
          >
            {truckImages.map((image, index) => (
              <img
                key={image}
                src={image}
                alt='S Infinite Express logistics operations'
                className={`
                  absolute inset-0
                  h-full w-full
                  object-cover object-center
                  transition-opacity duration-[1500ms]
                  ease-in-out
                  ${
                    currentImage === index
                      ? 'z-[1] opacity-100'
                      : 'z-0 opacity-0'
                  }
                `}
              />
            ))}

            {/* Image overlay */}
            <div
              className='
              pointer-events-none
              absolute inset-0 z-[2]
              bg-gradient-to-t
              from-[#171b50]/30
              via-transparent
              to-transparent
            '
            />
          </div>

          {/* Slider indicators */}
          <div
            className='
            absolute bottom-5
            right-5 sm:right-8
            z-10
            flex items-center gap-2
          '
          >
            {truckImages.map((_, index) => (
              <button
                key={index}
                type='button'
                onClick={() => setCurrentImage(index)}
                aria-label={`Show image ${index + 1}`}
                aria-pressed={currentImage === index}
                className={`
                  h-[4px]
                  rounded-full
                  transition-all duration-300
                  ${
                    currentImage === index
                      ? 'w-8 bg-[#ffe331]'
                      : 'w-3 bg-white/60 hover:bg-white'
                  }
                `}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default WhyChooseUs
